import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import {
  Button,
  DetailSection,
  SkeletonText,
  StatusBadge,
  Timestamp,
  useFocusTrap,
} from "@/design-system/components/ui";
import { formatPhone } from "@/design-system/lib/format";
import {
  prequalSiteApi,
  type PrequalAnswerDisplayItem,
  type PrequalCompletionDetail,
  type PrequalDocument,
} from "../../api/prequalSite";
import {
  PREQUAL_ANSWER_GROUP_LABELS,
  PREQUAL_ANSWER_GROUP_ORDER,
  errorMessage,
  outcomeLabel,
  outcomeTone,
  type CompletionDetailDrawerProps,
} from "./panelProps";

// Partner Brand Ownership — the `?completion=` drill-in, as a right-side
// slide-in drawer.
//
// It used to replace the ENTIRE tab, tab bar included (D11), and showed contact
// fields over a bare document list with no links. Two things changed:
//
//   1. It is a drawer. The list stays mounted behind it, so closing is a return
//      to the row the partner was on rather than a re-navigation that costs them
//      their page, their search and their scope. The trigger and the deep link
//      are untouched — `?completion=` still opens it on load, and the shell
//      still clears only that one key so `?view=` survives.
//   2. It renders the ANSWERS. The server's explainer registry returns a flat,
//      ordered, already-explained row set; this file groups it and prints it.
//
// 🔴 THE RENDERING RULE. Every value here arrives formatted and every
// explanation arrives chosen. This component does no tier logic, no currency /
// date / percentage formatting of answer values, and no conditional wording. A
// figure re-derived on the client is a figure that can disagree with the Letter
// the applicant is already holding — the server reads the stored snapshot,
// which is deliberately never recomputed. `tier` is carried on the row for a
// test to pin and for a future surface to use; this one does not branch on it,
// because the presence of `explanation` already says everything the tier would.
//
// Mechanics mirror `portal/components/MaterialPreviewDrawer` — the portal's own
// drawer, which gets the accessibility right that the shared staff `Modal` does
// not: initial focus, a real Tab cycle, focus restored to the trigger, Escape,
// backdrop close, and `role="dialog"` on the panel rather than the overlay.
// Focus itself comes from the shared `useFocusTrap` rather than a second
// hand-rolled copy of it.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only, and no React Query.

/** One printed row. `value` is a ReactNode rather than a string so the drawer's
 *  own identity rows can use `<Timestamp>` — every row that came from the server
 *  passes its `displayValue` through untouched. */
interface DrawerRow {
  key: string;
  label: string;
  value: ReactNode;
  /** Server-chosen, or absent. NEVER composed here. */
  explanation?: string | null;
}

function toRow(item: PrequalAnswerDisplayItem): DrawerRow {
  return {
    key: item.fieldKey,
    label: item.label,
    value: item.displayValue,
    explanation: item.explanation,
  };
}

/** WHO THIS IS — rendered by the drawer, from the DTO's own typed members.
 *
 *  🔴 The registry CANNOT emit these, and that is correct rather than a gap:
 *  `email`, `phone`, `referenceNumber` and `validThroughUtc` are columns on the
 *  submission row, not entries in `AnswersJson` / `EngineInputsJson`, and the
 *  registry is handed only the JSON. Emitting them would mean printing its
 *  "Not recorded" sentinel — which means "absent from an older snapshot" —
 *  against an email address we demonstrably hold.
 *
 *  So they are prepended to the server's own `About` rows, which is why that
 *  section can never collapse to empty even though a buyer's `About` is only
 *  two rows. The name and the outcome are in the drawer HEADER; the track and
 *  the completion date are in the header's meta line.
 *
 *  🔴 NOT the qualified amount / supportable price. The registry sends those as
 *  `result.qualifiedAmount` and `result.supportablePriceBand` in "What we worked
 *  out", each with a sentence it chose — and a soft decline gets one honest
 *  "Not sized" row instead of a column of dashes. A second rendering here is
 *  exactly the duplicate figure this surface exists to avoid.
 *
 *  Keys carry a `detail.` prefix. The registry's are `answers.` / `result.` /
 *  `calculated.` / `screening.` / `dealSheet.`, so a collision is structurally
 *  impossible rather than merely unobserved. */
function identityRows(detail: PrequalCompletionDetail): DrawerRow[] {
  const rows: DrawerRow[] = [
    { key: "detail.email", label: "Email", value: detail.email },
    // A dash rather than an omission: whether we hold a phone number is itself
    // what a partner about to call is checking.
    { key: "detail.phone", label: "Phone", value: detail.phone ? formatPhone(detail.phone) : "—" },
    { key: "detail.reference", label: "Reference", value: detail.referenceNumber ?? "—" },
  ];
  // Omitted rather than dashed when absent. A soft decline is issued no letter
  // and so has no validity window; "Valid through —" reads as a data fault
  // instead of the deliberate absence it is, and the screening section already
  // says what happened.
  if (detail.validThroughUtc) {
    rows.push({
      key: "detail.validThrough",
      label: "Valid through",
      value: <Timestamp value={detail.validThroughUtc} absolute />,
    });
  }
  return rows;
}

/** Label, value, and — for the two tiers that carry one — the sentence the
 *  registry chose. Nothing conditional happens here beyond printing the
 *  explanation when there is one. */
function Row({ row }: { row: DrawerRow }) {
  return (
    <div
      className="grid grid-cols-1 gap-x-space-3 gap-y-space-1 border-t border-border-light py-space-2 first:border-t-0 first:pt-0 sm:grid-cols-[minmax(0,220px)_minmax(0,1fr)]"
      data-testid={`prequal-answer-${row.key}`}
    >
      <dt className="m-0 text-caption uppercase tracking-wide text-gray-mid">{row.label}</dt>
      <dd className="m-0 min-w-0">
        <span className="block break-words text-body text-onyx">{row.value}</span>
        {row.explanation && (
          <span
            className="mt-space-1 block text-small text-gray-mid"
            data-testid={`prequal-answer-why-${row.key}`}
          >
            {row.explanation}
          </span>
        )}
      </dd>
    </div>
  );
}

function AnswerGroupSection({
  title,
  rows,
  testId,
  children,
}: {
  title: ReactNode;
  rows: DrawerRow[];
  testId: string;
  children?: ReactNode;
}) {
  return (
    <DetailSection title={title} data-testid={testId}>
      {rows.length > 0 && (
        <dl className="m-0 flex flex-col">
          {rows.map((row) => (
            <Row key={row.key} row={row} />
          ))}
        </dl>
      )}
      {children}
    </DetailSection>
  );
}

export function CompletionDetailDrawer({ completionId, onClose }: CompletionDetailDrawerProps) {
  const [detail, setDetail] = useState<PrequalCompletionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Scoped to the <aside>, so the close control IS inside the cycle — closing
  // must never depend on reaching something the trap excludes.
  const trap = useFocusTrap<HTMLElement>({ active: true });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await prequalSiteApi.completion(completionId);
        if (!cancelled) setDetail(result);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this prequalification."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [completionId]);

  // Mounting IS opening — the shell mounts this conditionally, so there is no
  // `open` flag to disagree with. Focus capture and restore belong to the trap.
  //
  // The scroll lock is on its own effect keyed on NOTHING: the shell's
  // `onClose` is a fresh closure every render, so sharing an effect with the
  // Escape listener would tear the lock down and put it back on every re-render
  // of the tab behind this.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const sections = useMemo(() => groupAnswers(detail?.answers ?? []), [detail]);

  const title = detail ? `${detail.firstName} ${detail.lastName}` : "Prequalification";

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-onyx/45 backdrop-blur-[1px]"
      role="presentation"
      onClick={onClose}
      data-testid="prequal-detail-overlay"
    >
      {/* w-full + a max-w cap IS the mobile behaviour: full width on a phone,
          capped on a desktop. No breakpoint prefix on the width. */}
      <aside
        ref={trap.containerRef}
        className="flex h-full w-full max-w-4xl animate-slide-in-right flex-col border-l-[2px] border-l-gold bg-white-pure shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prequal-detail-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trap.onKeyDown}
        data-testid="prequal-detail-drawer"
      >
        <header className="flex items-start justify-between gap-space-3 border-b border-border-light p-space-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-space-2">
              <h2
                id="prequal-detail-title"
                className="m-0 break-words font-display text-h3 font-bold tracking-normal text-onyx"
              >
                {title}
              </h2>
              {detail && (
                <StatusBadge tone={outcomeTone(detail.outcome)}>
                  {outcomeLabel(detail.outcome)}
                </StatusBadge>
              )}
            </div>
            {detail && (
              <p className="m-0 mt-space-1 text-caption text-gray-mid">
                {detail.role}
                {detail.referenceNumber && <> · {detail.referenceNumber}</>}
                {" · "}
                <Timestamp value={detail.completedAtUtc} />
              </p>
            )}
          </div>
          {/* "Close details", not "Close": the footer carries a visible Close
              button, and two controls answering to the same accessible name is
              an ambiguous by-role query in every test that touches this. */}
          <button
            ref={trap.setInitialFocus}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-space-1 hover:bg-white-warm focus-visible:outline-none focus-visible:shadow-goldRing"
            aria-label="Close details"
            data-testid="prequal-detail-close"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        {/* ~100 rows on a full submission — the scroll is the point of the
            drawer, not an afterthought. */}
        <div className="flex flex-1 flex-col gap-space-4 overflow-y-auto bg-white-warm/35 p-space-4">
          {error ? (
            <p className="m-0 text-small text-status-error" role="alert">
              {error}
            </p>
          ) : !detail ? (
            <SkeletonText lines={6} data-testid="prequal-detail-loading" />
          ) : (
            <>
              {/* One ordered pass over the server's own section list. TWO of
                  those sections have rows the drawer contributes — `About` gets
                  the identity fields the registry cannot see, `Documents` gets
                  the generated artifacts — so those two always render; every
                  other section is omitted entirely when it has no rows, never
                  rendered empty. */}
              {PREQUAL_ANSWER_GROUP_ORDER.map((group) => {
                const leading = group === "About" ? identityRows(detail) : [];
                const rows = [...leading, ...(sections.buckets.get(group) ?? []).map(toRow)];
                const isDrawerOwned = group === "About" || group === "Documents";
                if (rows.length === 0 && !isDrawerOwned) return null;
                return (
                  <AnswerGroupSection
                    key={group}
                    title={PREQUAL_ANSWER_GROUP_LABELS[group]}
                    rows={rows}
                    testId={`prequal-answer-group-${group}`}
                  >
                    {group === "Documents" && (
                      <DocumentList completionId={completionId} documents={detail.documents} />
                    )}
                  </AnswerGroupSection>
                );
              })}

              {/* 🔴 An unrecognized group is a BUG — the producer and this
                  renderer have come to disagree about a section name. Surfaced
                  loudly rather than sorted silently to the end or dropped: the
                  rows render either way, and a reviewer can see which. */}
              {sections.unknown.map((section) => (
                <AnswerGroupSection
                  key={section.group}
                  title={`Unrecognized section: ${section.group}`}
                  rows={section.items.map(toRow)}
                  testId="prequal-answer-group-unknown"
                >
                  <p className="m-0 mt-space-2 text-caption text-status-error">
                    These answers arrived in a section this screen doesn't know about. Let Phoenix
                    know — nothing has been hidden, but it may be in the wrong place.
                  </p>
                </AnswerGroupSection>
              ))}
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-space-2 border-t border-border-light bg-white-pure p-space-4">
          <Button type="button" variant="secondary" onClick={onClose} data-testid="prequal-back">
            Close
          </Button>
        </footer>
      </aside>
    </div>
  );
}

/** The generated artifacts, each with a download. Only Letter and Deal Sheet
 *  ever reach a partner — the server filters the internal Analysis out, so
 *  there is nothing to branch on here. */
function DocumentList({
  completionId,
  documents,
}: {
  completionId: string;
  documents: PrequalDocument[];
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(
    async (documentId: string) => {
      setBusyId(documentId);
      setError(null);
      try {
        const link = await prequalSiteApi.completionDocumentLink(completionId, documentId);
        // The URL is short-lived and presigned, which is why it is fetched here
        // rather than carried on the detail payload. No null check on the
        // return: `noopener` makes `window.open` return null BY SPEC, so
        // treating that as a failure would report one on every success.
        window.open(link.url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setError(errorMessage(err, "Couldn't open that document. Try again in a moment."));
      } finally {
        setBusyId(null);
      }
    },
    [completionId],
  );

  if (documents.length === 0) {
    return (
      <p className="m-0 text-small text-gray-mid">
        No documents were generated for this prequalification.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-space-2">
      {error && (
        <p className="m-0 text-small text-status-error" role="alert">
          {error}
        </p>
      )}
      <ul className="m-0 flex list-none flex-col gap-space-2 p-0">
        {documents.map((doc) => (
          <li
            key={doc.documentId}
            className="flex flex-wrap items-center justify-between gap-space-2 text-body text-onyx"
          >
            <span className="min-w-0">
              {docTypeLabel(doc.docType)}
              <span className="ml-space-2 text-caption text-gray-mid">
                <Timestamp value={doc.generatedAtUtc} />
              </span>
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busyId === doc.documentId}
              onClick={() => void download(doc.documentId)}
              data-testid={`prequal-doc-download-${doc.documentId}`}
            >
              {busyId === doc.documentId ? "Opening…" : "Download"}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function docTypeLabel(docType: string): string {
  return docType === "DealSheet" ? "Deal sheet" : docType;
}

/** Buckets the flat row set by `group`.
 *
 *  Order is NOT applied here — the render walks the shared group list, so the
 *  sequence comes from the server's own vocabulary rather than from the arrival
 *  order of the rows or from a sort (which would file an unrecognized group at
 *  one end silently). Within a bucket the server's order is preserved untouched.
 *
 *  A row the registry unexpectedly stamped `Documents` lands in that bucket and
 *  prints in that section, above the artifacts — it is not the drawer's to drop
 *  just because it does not expect it. */
function groupAnswers(items: PrequalAnswerDisplayItem[]): {
  buckets: Map<string, PrequalAnswerDisplayItem[]>;
  unknown: { group: string; items: PrequalAnswerDisplayItem[] }[];
} {
  const buckets = new Map<string, PrequalAnswerDisplayItem[]>();
  for (const item of items) {
    const bucket = buckets.get(item.group);
    if (bucket) bucket.push(item);
    else buckets.set(item.group, [item]);
  }

  const unknown = [...buckets.entries()]
    .filter(([group]) => !(PREQUAL_ANSWER_GROUP_ORDER as readonly string[]).includes(group))
    .map(([group, groupItems]) => ({ group, items: groupItems }));

  return { buckets, unknown };
}
